import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  MessageSquare, Users, Tag, Settings, Upload, Play, Pause,
  Plus, Trash2, Edit2, Search, Filter, RefreshCw, Save,
  Image, Video, FileText, CheckCircle, XCircle, Clock,
  ChevronRight, Copy, Eye, Loader2, Send, BarChart3,
  Calendar, Download, X, ChevronLeft, AlertCircle, Zap,
  Phone, UserPlus, FileSpreadsheet, Target, TrendingUp,
  ChevronDown, Layers, AlertTriangle, Percent, Info
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from '../../hooks/use-toast';
import api from '../../services/api';

const ATTRIBUTE_TYPES = [
  { value: 'string', label: 'Text', icon: '📝' },
  { value: 'integer', label: 'Number', icon: '🔢' },
  { value: 'decimal', label: 'Decimal', icon: '💯' },
  { value: 'email', label: 'Email', icon: '📧' },
  { value: 'date', label: 'Date', icon: '📅' },
  { value: 'phone', label: 'Phone', icon: '📱' },
  { value: 'boolean', label: 'Yes/No', icon: '✅' },
];

const TAG_COLORS = [
  '#6366f1', '#8b5cf6', '#d946ef', '#ec4899', '#f43f5e',
  '#ef4444', '#f97316', '#f59e0b', '#22c55e', '#10b981',
  '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6', '#6b7280'
];

// Format date to IST
const formatIST = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(typeof dateStr === 'number' ? dateStr : dateStr);
  return date.toLocaleString('en-IN', { 
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// WhatsApp-style Template Card - Works for both IMAGE and TEXT
const TemplateCard = ({ template, selected, onSelect, selectable = true }) => {
  const [imageError, setImageError] = useState(false);
  const type = template.type || 'TEXT';
  const headerUrl = template.header_url || template.media_url || template.example_media_url;
  const hasMedia = ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(type);

  const renderText = (text) => {
    if (!text) return null;
    // Show full text without truncation
    const parts = text.split(/(\{\{\d+\}\}|\*[^*]+\*)/g);
    return parts.map((part, idx) => {
      if (/\{\{\d+\}\}/.test(part)) {
        return <span key={idx} className="bg-[#dcf8c6] text-emerald-800 px-0.5 rounded text-[10px]">{part}</span>;
      }
      if (/^\*[^*]+\*$/.test(part)) {
        return <strong key={idx}>{part.slice(1, -1)}</strong>;
      }
      return <span key={idx}>{part}</span>;
    });
  };

  return (
    <div
      className={`border rounded-xl overflow-hidden cursor-pointer transition-all hover:shadow-lg flex flex-col ${
        selected ? 'ring-2 ring-emerald-500 border-emerald-500' : 'border-slate-200'
      }`}
      onClick={() => selectable && onSelect?.(template)}
    >
      {/* WhatsApp Chat Background */}
      <div 
        className="p-2 flex-1 min-h-[120px]"
        style={{
          backgroundColor: '#e5ddd5',
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d4ccc4' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}
      >
        {/* Message Bubble */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden max-w-full">
          {/* Media Preview */}
          {hasMedia && (
            <div className="relative">
              {type === 'IMAGE' && (
                headerUrl && !imageError ? (
                  <img src={headerUrl} alt="" className="w-full h-20 object-cover" onError={() => setImageError(true)} />
                ) : (
                  <div className="w-full h-20 bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
                    <Image className="w-6 h-6 text-blue-400" />
                  </div>
                )
              )}
              {type === 'VIDEO' && (
                <div className="w-full h-20 bg-slate-800 flex items-center justify-center relative">
                  <div className="w-8 h-8 bg-white/90 rounded-full flex items-center justify-center">
                    <Play className="w-4 h-4 text-slate-800 ml-0.5" fill="currentColor" />
                  </div>
                </div>
              )}
              {type === 'DOCUMENT' && (
                <div className="w-full h-16 bg-gradient-to-br from-amber-50 to-orange-100 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-orange-500" />
                </div>
              )}
            </div>
          )}
          {/* Text Content */}
          <div className="p-2">
            <p className="text-[11px] text-slate-800 whitespace-pre-wrap leading-snug break-words">
              {renderText(template.text || template.sample_text || template.body_text)}
            </p>
          </div>
          {/* Buttons Preview */}
          {template.buttons?.length > 0 && (
            <div className="border-t">
              {template.buttons.slice(0, 2).map((btn, idx) => (
                <div key={idx} className="text-center py-1.5 border-b last:border-b-0 text-blue-500 text-[10px] font-medium">
                  {btn.button_title || btn.text}
                </div>
              ))}
            </div>
          )}
          {/* Timestamp */}
          <div className="px-2 pb-1 flex justify-end">
            <span className="text-[9px] text-slate-400">12:00 PM</span>
          </div>
        </div>
      </div>
      
      {/* Info Footer */}
      <div className="p-2 bg-white border-t space-y-1">
        <div className="flex items-center justify-between gap-1">
          <h4 className="font-medium text-xs truncate flex-1" title={template.name}>{template.name}</h4>
          <Badge className={`text-[8px] px-1 py-0 ${
            template.status === 'APPROVED' ? 'bg-emerald-100 text-emerald-700' :
            template.status === 'PAUSED' ? 'bg-amber-100 text-amber-700' :
            'bg-red-100 text-red-700'
          }`}>{template.status}</Badge>
        </div>
        <div className="flex items-center justify-between text-[9px] text-slate-500">
          <div className="flex gap-1">
            <Badge variant="outline" className="text-[8px] px-1 py-0">{type}</Badge>
            {template.category && <Badge variant="outline" className="text-[8px] px-1 py-0">{template.category}</Badge>}
          </div>
          <span>{template.language}</span>
        </div>
        {template.created_at && (
          <p className="text-[8px] text-slate-400">{formatIST(template.created_at)}</p>
        )}
      </div>
    </div>
  );
};

// WhatsApp Preview with Dynamic Variables
const WhatsAppPreview = ({ template, variables, mediaUrl, contact }) => {
  const renderText = (text) => {
    if (!text) return '';
    let result = text;
    // Replace {{n}} with variable values or attribute placeholders
    Object.entries(variables || {}).forEach(([key, value]) => {
      let finalValue = value || `{{${key}}}`;
      // Replace $attribute with contact values
      if (contact && value) {
        finalValue = value.replace(/\$(\w+)/g, (match, attr) => {
          if (attr.toLowerCase() === 'name') return contact.name || match;
          if (attr.toLowerCase() === 'phone') return contact.phone || match;
          return contact.attributes?.[attr] || match;
        });
      }
      result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), finalValue);
    });
    // Bold text
    result = result.replace(/\*([^*]+)\*/g, '<strong>$1</strong>');
    return result;
  };

  const type = template?.type || 'TEXT';
  const finalMediaUrl = mediaUrl || template?.header_url || template?.example_media_url;

  return (
    <div 
      className="rounded-xl p-4 max-w-sm"
      style={{
        backgroundColor: '#e5ddd5',
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d4ccc4' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
      }}
    >
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {type === 'IMAGE' && finalMediaUrl && (
          <img src={finalMediaUrl} alt="Media" className="w-full h-40 object-cover" />
        )}
        {type === 'VIDEO' && (
          <div className="w-full h-40 bg-slate-900 flex items-center justify-center relative">
            <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center">
              <Play className="w-6 h-6 text-slate-800 ml-0.5" fill="currentColor" />
            </div>
          </div>
        )}
        {type === 'DOCUMENT' && (
          <div className="p-4 bg-slate-50 flex items-center gap-3 border-b">
            <FileText className="w-10 h-10 text-orange-500" />
            <div><p className="font-medium text-sm">Document.pdf</p></div>
          </div>
        )}
        <div className="p-3">
          <p className="text-sm text-slate-800 whitespace-pre-wrap" 
             dangerouslySetInnerHTML={{ __html: renderText(template?.text || template?.sample_text) }} />
        </div>
        {template?.buttons?.length > 0 && (
          <div className="border-t">
            {template.buttons.map((btn, idx) => (
              <div key={idx} className="text-center py-2.5 border-b last:border-b-0 text-blue-500 text-sm font-medium">
                {btn.button_title || btn.text}
              </div>
            ))}
          </div>
        )}
        <div className="px-3 pb-2 flex justify-end">
          <span className="text-[10px] text-slate-400">
            {new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
          </span>
        </div>
      </div>
    </div>
  );
};

// Campaign Status Badge
const CampaignStatusBadge = ({ status }) => {
  const styles = {
    draft: 'bg-slate-100 text-slate-700',
    scheduled: 'bg-blue-100 text-blue-700',
    running: 'bg-emerald-100 text-emerald-700 animate-pulse',
    paused: 'bg-amber-100 text-amber-700',
    completed: 'bg-green-100 text-green-700',
    failed: 'bg-red-100 text-red-700',
  };
  return <Badge className={styles[status] || styles.draft}>{status?.toUpperCase()}</Badge>;
};

const WACampaignsPage = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('templates');
  
  // Config
  const [apiConfig, setApiConfig] = useState({});
  const [configForm, setConfigForm] = useState({});
  const [savingConfig, setSavingConfig] = useState(false);
  
  // Templates - Auto-load with pagination
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templatesTotal, setTemplatesTotal] = useState(0);
  const [templatesHasMore, setTemplatesHasMore] = useState(false);
  const [templatesSkip, setTemplatesSkip] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Attributes
  const [attributes, setAttributes] = useState([]);
  const [attributeDialog, setAttributeDialog] = useState(false);
  const [editingAttribute, setEditingAttribute] = useState(null);
  const [attributeForm, setAttributeForm] = useState({});
  
  // Tags
  const [tags, setTags] = useState([]);
  const [tagDialog, setTagDialog] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [tagForm, setTagForm] = useState({ color: '#6366f1' });
  
  // Contacts with advanced filters
  const [contacts, setContacts] = useState([]);
  const [contactsTotal, setContactsTotal] = useState(0);
  const [contactsPage, setContactsPage] = useState(0);
  const [contactsLimit, setContactsLimit] = useState(100);
  const [contactFilters, setContactFilters] = useState({});
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  
  // Upload
  const [uploadDialog, setUploadDialog] = useState(false);
  const [uploadForm, setUploadForm] = useState({});
  const [csvColumns, setCsvColumns] = useState([]);
  const [csvFile, setCsvFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Contact dialog
  const [contactDialog, setContactDialog] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactForm, setContactForm] = useState({});
  
  // Delete confirm
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Campaigns
  const [campaigns, setCampaigns] = useState([]);
  const [campaignDialog, setCampaignDialog] = useState(false);
  const [campaignStep, setCampaignStep] = useState(1);
  const [campaignForm, setCampaignForm] = useState({
    name: '', description: '', is_multi_template: false,
    template_variables: {}, media_url: '', audience_type: 'selected',
    audience_filter: {}, scheduled_at: null, fallback_values: {}
  });
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [campaignContacts, setCampaignContacts] = useState([]);
  const [campaignTemplates, setCampaignTemplates] = useState([]);
  const [campaignTemplatesHasMore, setCampaignTemplatesHasMore] = useState(false);
  const [campaignTemplatesSkip, setCampaignTemplatesSkip] = useState(0);
  const [validationResult, setValidationResult] = useState(null);
  const [validating, setValidating] = useState(false);
  
  // Campaign Analytics Dialog
  const [analyticsDialog, setAnalyticsDialog] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsContactsPage, setAnalyticsContactsPage] = useState(0);

  // Fetch initial data
  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [configRes, attrsRes, tagsRes, campaignsRes] = await Promise.all([
        api.get('/api/wa-campaigns/config'),
        api.get('/api/wa-campaigns/attributes'),
        api.get('/api/wa-campaigns/tags'),
        api.get('/api/wa-campaigns/campaigns')
      ]);
      
      setApiConfig(configRes.data);
      setConfigForm({
        send_api_url: configRes.data.send_api_url || '',
        templates_api_url: configRes.data.templates_api_url || '',
        api_headers: configRes.data.api_headers || [],
        api_key: '',
        api_key_header: configRes.data.api_key_header || 'Authorization',
        api_key_prefix: configRes.data.api_key_prefix || 'Bearer',
        max_requests_per_second: configRes.data.max_requests_per_second || 80,
        rate_limit_pause_seconds: configRes.data.rate_limit_pause_seconds || 10,
        rate_limit_resume_mps: configRes.data.rate_limit_resume_mps || 78
      });
      
      setAttributes(attrsRes.data.attributes || []);
      setTags(tagsRes.data.tags || []);
      setCampaigns(campaignsRes.data.campaigns || []);
      
      // Auto-fetch templates
      await fetchTemplates(0, false);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast({ variant: 'destructive', title: 'Failed to load data' });
    } finally {
      setLoading(false);
    }
  };

  // Fetch templates with pagination
  const fetchTemplates = async (skip = 0, append = false) => {
    if (skip === 0) setLoadingTemplates(true);
    else setLoadingMore(true);
    
    try {
      const res = await api.get(`/api/wa-campaigns/templates?skip=${skip}&limit=100`);
      const newTemplates = res.data.templates || [];
      
      if (append) {
        setTemplates(prev => [...prev, ...newTemplates]);
      } else {
        setTemplates(newTemplates);
      }
      
      setTemplatesTotal(res.data.total || res.data.count || newTemplates.length);
      setTemplatesHasMore(res.data.has_more || (res.data.count && res.data.count > skip + 100));
      setTemplatesSkip(skip + newTemplates.length);
      
      if (!append && skip === 0) {
        toast({ title: `Loaded ${res.data.total || res.data.count || newTemplates.length} templates` });
      }
    } catch (error) {
      toast({ variant: 'destructive', title: 'Failed to fetch templates', description: error.response?.data?.detail });
    } finally {
      setLoadingTemplates(false);
      setLoadingMore(false);
    }
  };

  // Auto-fetch templates when switching to templates tab
  useEffect(() => {
    if (activeTab === 'templates' && templates.length === 0 && !loadingTemplates) {
      fetchTemplates(0, false);
    }
  }, [activeTab, templates.length, loadingTemplates]);

  // Fetch templates for campaign dialog
  const fetchCampaignTemplates = async (skip = 0, append = false) => {
    try {
      const res = await api.get(`/api/wa-campaigns/templates?skip=${skip}&limit=100`);
      const newTemplates = res.data.templates || [];
      
      if (append) {
        setCampaignTemplates(prev => [...prev, ...newTemplates]);
      } else {
        setCampaignTemplates(newTemplates);
      }
      
      setCampaignTemplatesHasMore(res.data.has_more || (res.data.count && res.data.count > skip + 100));
      setCampaignTemplatesSkip(skip + newTemplates.length);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  // Fetch contacts with advanced filters
  const fetchContacts = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        skip: String(contactsPage * contactsLimit),
        limit: String(contactsLimit)
      });
      
      if (contactFilters.phone) params.append('phone', contactFilters.phone);
      if (contactFilters.name) params.append('name', contactFilters.name);
      if (contactFilters.tags?.length) params.append('tags', contactFilters.tags.join(','));
      
      // Advanced attribute filters
      if (contactFilters.attributes) {
        Object.entries(contactFilters.attributes).forEach(([attrId, value]) => {
          if (value) params.append(`attr_${attrId}`, value);
        });
      }
      
      const res = await api.get(`/api/wa-campaigns/contacts?${params}`);
      setContacts(res.data.contacts || []);
      setContactsTotal(res.data.total || 0);
    } catch (error) {
      toast({ variant: 'destructive', title: 'Failed to fetch contacts' });
    }
  }, [contactsPage, contactsLimit, contactFilters]);

  useEffect(() => {
    if (activeTab === 'contacts') {
      fetchContacts();
    }
  }, [activeTab, fetchContacts]);

  // Config handlers
  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      await api.patch('/api/wa-campaigns/config', configForm);
      toast({ title: 'Configuration saved' });
      const res = await api.get('/api/wa-campaigns/config');
      setApiConfig(res.data);
    } catch (error) {
      toast({ variant: 'destructive', title: 'Failed to save', description: error.response?.data?.detail });
    } finally {
      setSavingConfig(false);
    }
  };

  // Attribute handlers
  const saveAttribute = async () => {
    try {
      if (editingAttribute) {
        await api.patch(`/api/wa-campaigns/attributes/${editingAttribute.id}`, attributeForm);
        toast({ title: 'Attribute updated' });
      } else {
        await api.post('/api/wa-campaigns/attributes', attributeForm);
        toast({ title: 'Attribute created' });
      }
      setAttributeDialog(false);
      setEditingAttribute(null);
      setAttributeForm({});
      const res = await api.get('/api/wa-campaigns/attributes');
      setAttributes(res.data.attributes || []);
    } catch (error) {
      toast({ variant: 'destructive', title: 'Failed', description: error.response?.data?.detail });
    }
  };

  const deleteAttribute = async (id) => {
    try {
      await api.delete(`/api/wa-campaigns/attributes/${id}`);
      toast({ title: 'Deleted' });
      setAttributes(attrs => attrs.filter(a => a.id !== id));
    } catch (error) {
      toast({ variant: 'destructive', title: 'Failed' });
    }
    setDeleteConfirm(null);
  };

  // Tag handlers
  const saveTag = async () => {
    try {
      if (editingTag) {
        await api.patch(`/api/wa-campaigns/tags/${editingTag.id}`, tagForm);
        toast({ title: 'Tag updated' });
      } else {
        await api.post('/api/wa-campaigns/tags', tagForm);
        toast({ title: 'Tag created' });
      }
      setTagDialog(false);
      setEditingTag(null);
      setTagForm({ color: '#6366f1' });
      const res = await api.get('/api/wa-campaigns/tags');
      setTags(res.data.tags || []);
    } catch (error) {
      toast({ variant: 'destructive', title: 'Failed', description: error.response?.data?.detail });
    }
  };

  const deleteTag = async (id) => {
    try {
      await api.delete(`/api/wa-campaigns/tags/${id}`);
      toast({ title: 'Deleted' });
      setTags(t => t.filter(tag => tag.id !== id));
    } catch (error) {
      toast({ variant: 'destructive', title: 'Failed' });
    }
    setDeleteConfirm(null);
  };

  // Contact handlers
  const saveContact = async () => {
    try {
      if (editingContact) {
        await api.patch(`/api/wa-campaigns/contacts/${editingContact.id}`, contactForm);
        toast({ title: 'Contact updated' });
      } else {
        await api.post('/api/wa-campaigns/contacts', contactForm);
        toast({ title: 'Contact created' });
      }
      setContactDialog(false);
      setEditingContact(null);
      setContactForm({});
      fetchContacts();
    } catch (error) {
      toast({ variant: 'destructive', title: 'Failed', description: error.response?.data?.detail });
    }
  };

  const deleteContact = async (contact) => {
    try {
      await api.delete(`/api/wa-campaigns/contacts/${contact.id}`);
      toast({ title: 'Deleted' });
      fetchContacts();
      setSelectedContacts(prev => prev.filter(c => c.id !== contact.id));
    } catch (error) {
      toast({ variant: 'destructive', title: 'Failed' });
    }
    setDeleteConfirm(null);
  };

  const toggleContactSelection = (contact) => {
    setSelectedContacts(prev => {
      const exists = prev.find(c => c.id === contact.id);
      return exists ? prev.filter(c => c.id !== contact.id) : [...prev, contact];
    });
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedContacts([]);
      setSelectAll(false);
    } else {
      setSelectedContacts([...contacts]);
      setSelectAll(true);
    }
  };

  // CSV Upload
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvFile(file);
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result;
      const lines = text.split('\n');
      const columns = lines[0].split(',').map(col => col.trim().replace(/"/g, ''));
      setCsvColumns(columns);
      const preview = [];
      for (let i = 1; i < Math.min(6, lines.length); i++) {
        if (lines[i].trim()) {
          const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
          const row = {};
          columns.forEach((col, idx) => { row[col] = values[idx] || ''; });
          preview.push(row);
        }
      }
      setCsvPreview(preview);
    };
    reader.readAsText(file);
  };

  const uploadContacts = async () => {
    if (!csvFile || !uploadForm.phone_column) {
      toast({ variant: 'destructive', title: 'Select file and phone column' });
      return;
    }
    setUploading(true);
    setUploadProgress(10);
    const formData = new FormData();
    formData.append('file', csvFile);
    formData.append('phone_column', uploadForm.phone_column);
    if (uploadForm.name_column && uploadForm.name_column !== 'none') formData.append('name_column', uploadForm.name_column);
    if (uploadForm.tag_ids?.length) formData.append('tag_ids', uploadForm.tag_ids.join(','));
    if (uploadForm.attribute_mapping) {
      const filtered = {};
      Object.entries(uploadForm.attribute_mapping).forEach(([k, v]) => { if (v && v !== 'none') filtered[k] = v; });
      if (Object.keys(filtered).length > 0) formData.append('attribute_mapping', JSON.stringify(filtered));
    }
    try {
      setUploadProgress(30);
      const res = await api.post('/api/wa-campaigns/contacts/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => setUploadProgress(Math.min(Math.round((e.loaded * 70) / e.total) + 30, 95))
      });
      setUploadProgress(100);
      toast({ title: 'Upload completed', description: `Success: ${res.data.success}, Duplicates: ${res.data.duplicates}, Failed: ${res.data.failed}` });
      setUploadDialog(false);
      setCsvFile(null);
      setCsvColumns([]);
      setCsvPreview([]);
      setUploadForm({});
      fetchContacts();
    } catch (error) {
      toast({ variant: 'destructive', title: 'Upload failed', description: error.response?.data?.detail });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Template selection
  const toggleTemplateSelection = (template) => {
    setSelectedTemplates(prev => {
      const exists = prev.find(t => t.id === template.id);
      if (exists) return prev.filter(t => t.id !== template.id);
      if (campaignForm.is_multi_template && prev.length > 0) {
        const first = prev[0];
        if (`${first.type}_${first.total_parameters}` !== `${template.type}_${template.total_parameters}`) {
          toast({ variant: 'destructive', title: 'Incompatible template' });
          return prev;
        }
      }
      return campaignForm.is_multi_template ? [...prev, template] : [template];
    });
  };

  // Open campaign dialog
  const openCampaignDialog = (preSelectedContacts = []) => {
    resetCampaignForm();
    setCampaignContacts(preSelectedContacts);
    fetchCampaignTemplates(0, false);
    setCampaignDialog(true);
  };

  // Validate contacts for missing attributes
  const validateContacts = async () => {
    if (campaignContacts.length === 0 && campaignForm.audience_type === 'selected') return;
    setValidating(true);
    try {
      const contactIds = campaignContacts.map(c => c.id);
      const res = await api.post('/api/wa-campaigns/campaigns/validate-contacts', {
        contact_ids: contactIds,
        variable_text: campaignForm.template_variables
      });
      setValidationResult(res.data);
    } catch (error) {
      console.error('Validation failed:', error);
    } finally {
      setValidating(false);
    }
  };

  // Create campaign
  const createCampaign = async () => {
    try {
      const payload = {
        name: campaignForm.name,
        description: campaignForm.description,
        is_multi_template: campaignForm.is_multi_template,
        templates: selectedTemplates,
        template_variables: campaignForm.template_variables,
        fallback_values: campaignForm.fallback_values,
        media_url: campaignForm.media_url,
        media_type: selectedTemplates[0]?.type?.toLowerCase(),
        audience_type: campaignContacts.length > 0 ? 'selected' : campaignForm.audience_type,
        audience_filter: campaignForm.audience_filter,
        audience_contact_ids: campaignContacts.map(c => c.id),
        scheduled_at: campaignForm.scheduled_at
      };
      const res = await api.post('/api/wa-campaigns/campaigns', payload);
      toast({ title: 'Campaign created', description: `${res.data.total_contacts} contacts` });
      setCampaignDialog(false);
      resetCampaignForm();
      setSelectedContacts([]);
      refreshCampaigns();
    } catch (error) {
      toast({ variant: 'destructive', title: 'Failed', description: error.response?.data?.detail });
    }
  };

  const resetCampaignForm = () => {
    setCampaignForm({
      name: '', description: '', is_multi_template: false,
      template_variables: {}, media_url: '', audience_type: 'selected',
      audience_filter: {}, scheduled_at: null, fallback_values: {}
    });
    setSelectedTemplates([]);
    setCampaignContacts([]);
    setCampaignStep(1);
    setCampaignTemplates([]);
    setCampaignTemplatesSkip(0);
    setCampaignTemplatesHasMore(false);
    setValidationResult(null);
  };

  const startCampaign = async (id) => {
    try {
      await api.post(`/api/wa-campaigns/campaigns/${id}/start`);
      toast({ title: 'Campaign started' });
      setTimeout(refreshCampaigns, 2000);
    } catch (error) {
      toast({ variant: 'destructive', title: 'Failed', description: error.response?.data?.detail });
    }
  };

  const pauseCampaign = async (id) => {
    try {
      await api.post(`/api/wa-campaigns/campaigns/${id}/pause`);
      toast({ title: 'Campaign paused' });
      refreshCampaigns();
    } catch (error) {
      toast({ variant: 'destructive', title: 'Failed', description: error.response?.data?.detail });
    }
  };

  const refreshCampaigns = async () => {
    try {
      const res = await api.get('/api/wa-campaigns/campaigns');
      setCampaigns(res.data.campaigns || []);
    } catch (error) {
      console.error('Failed:', error);
    }
  };

  // Campaign Analytics
  const openAnalytics = async (campaign) => {
    setSelectedCampaign(campaign);
    setAnalyticsDialog(true);
    setAnalyticsLoading(true);
    setAnalyticsContactsPage(0);
    try {
      const res = await api.get(`/api/wa-campaigns/campaigns/${campaign.id}/analytics?skip=0&limit=50`);
      setAnalyticsData(res.data);
    } catch (error) {
      console.error('Failed:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const loadMoreAnalyticsContacts = async () => {
    if (!selectedCampaign || !analyticsData?.contacts?.has_more) return;
    const nextSkip = analyticsData.contacts.skip + analyticsData.contacts.limit;
    try {
      const res = await api.get(`/api/wa-campaigns/campaigns/${selectedCampaign.id}/analytics?skip=${nextSkip}&limit=50`);
      setAnalyticsData(prev => ({
        ...prev,
        contacts: {
          ...res.data.contacts,
          items: [...(prev?.contacts?.items || []), ...(res.data.contacts?.items || [])]
        }
      }));
    } catch (error) {
      console.error('Failed:', error);
    }
  };

  // Auto-refresh running campaigns
  useEffect(() => {
    const hasRunning = campaigns.some(c => c.status === 'running');
    if (hasRunning) {
      const interval = setInterval(refreshCampaigns, 5000);
      return () => clearInterval(interval);
    }
  }, [campaigns]);

  // Extract placeholders from variable text
  const extractedPlaceholders = useMemo(() => {
    const placeholders = new Set();
    Object.values(campaignForm.template_variables || {}).forEach(text => {
      if (text) {
        const matches = text.match(/\$(\w+)/g);
        if (matches) matches.forEach(m => placeholders.add(m.substring(1)));
      }
    });
    return Array.from(placeholders);
  }, [campaignForm.template_variables]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        <span className="ml-2 text-slate-500">Loading...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <MessageSquare className="w-7 h-7 text-emerald-600" />
            WhatsApp Campaigns
          </h1>
          <p className="text-slate-500">Manage templates, contacts, and campaigns</p>
        </div>
        <Button onClick={() => openCampaignDialog(selectedContacts)} className="bg-emerald-600 hover:bg-emerald-700">
          <Plus className="w-4 h-4 mr-2" /> New Campaign
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card><CardContent className="pt-4 flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg"><FileText className="w-5 h-5 text-blue-600" /></div>
          <div><p className="text-2xl font-bold">{templatesTotal}</p><p className="text-sm text-slate-500">Templates</p></div>
        </CardContent></Card>
        <Card><CardContent className="pt-4 flex items-center gap-3">
          <div className="p-2 bg-emerald-100 rounded-lg"><Users className="w-5 h-5 text-emerald-600" /></div>
          <div><p className="text-2xl font-bold">{contactsTotal.toLocaleString()}</p><p className="text-sm text-slate-500">Contacts</p></div>
        </CardContent></Card>
        <Card><CardContent className="pt-4 flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg"><Send className="w-5 h-5 text-purple-600" /></div>
          <div><p className="text-2xl font-bold">{campaigns.length}</p><p className="text-sm text-slate-500">Campaigns</p></div>
        </CardContent></Card>
        <Card><CardContent className="pt-4 flex items-center gap-3">
          <div className="p-2 bg-amber-100 rounded-lg"><Zap className="w-5 h-5 text-amber-600" /></div>
          <div><p className="text-2xl font-bold">{campaigns.filter(c => c.status === 'running').length}</p><p className="text-sm text-slate-500">Active</p></div>
        </CardContent></Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-6 w-full max-w-3xl">
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="contacts">Contacts</TabsTrigger>
          <TabsTrigger value="attributes">Attributes</TabsTrigger>
          <TabsTrigger value="tags">Tags</TabsTrigger>
          <TabsTrigger value="campaigns">Campaigns</TabsTrigger>
          <TabsTrigger value="config">Settings</TabsTrigger>
        </TabsList>

        {/* Templates Tab */}
        <TabsContent value="templates">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>WhatsApp Templates</CardTitle>
                <CardDescription>{templates.length} of {templatesTotal} loaded</CardDescription>
              </div>
              <Button onClick={() => fetchTemplates(0, false)} disabled={loadingTemplates}>
                {loadingTemplates ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Refresh
              </Button>
            </CardHeader>
            <CardContent>
              {templates.length === 0 ? (
                <div className="text-center py-16 text-slate-500">
                  <MessageSquare className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                  <p>No templates loaded</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                    {templates.filter(t => t.status === 'APPROVED').map(t => (
                      <TemplateCard key={t.id} template={t} selectable={false} />
                    ))}
                  </div>
                  {templatesHasMore && (
                    <div className="flex justify-center mt-6">
                      <Button variant="outline" onClick={() => fetchTemplates(templatesSkip, true)} disabled={loadingMore}>
                        {loadingMore ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ChevronDown className="w-4 h-4 mr-2" />}
                        Load More ({templatesTotal - templates.length} remaining)
                      </Button>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contacts Tab */}
        <TabsContent value="contacts">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Contact Book</CardTitle>
                  <CardDescription>{contactsTotal.toLocaleString()} contacts{selectedContacts.length > 0 && ` • ${selectedContacts.length} selected`}</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setUploadDialog(true)}><Upload className="w-4 h-4 mr-2" />Upload CSV</Button>
                  <Button variant="outline" onClick={() => { setEditingContact(null); setContactForm({}); setContactDialog(true); }}><UserPlus className="w-4 h-4 mr-2" />Add</Button>
                  <Button variant="outline" size="icon" onClick={fetchContacts}><RefreshCw className="w-4 h-4" /></Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Filters */}
              <div className="space-y-3 mb-4 p-3 bg-slate-50 rounded-lg">
                <div className="flex flex-wrap gap-3">
                  <Input placeholder="Phone" value={contactFilters.phone || ''} onChange={(e) => setContactFilters(f => ({ ...f, phone: e.target.value }))} className="w-40 h-9" />
                  <Input placeholder="Name" value={contactFilters.name || ''} onChange={(e) => setContactFilters(f => ({ ...f, name: e.target.value }))} className="w-40 h-9" />
                  <Select value={contactFilters.tags?.[0] || 'all'} onValueChange={(v) => setContactFilters(f => ({ ...f, tags: v === 'all' ? [] : [v] }))}>
                    <SelectTrigger className="w-40 h-9"><SelectValue placeholder="Tag" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Tags</SelectItem>
                      {tags.map(t => <SelectItem key={t.id} value={t.id}><div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full" style={{ backgroundColor: t.color }} />{t.name}</div></SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}>
                    <Filter className="w-4 h-4 mr-1" /> {showAdvancedFilters ? 'Hide' : 'More'} Filters
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => { setContactFilters({}); setContactsPage(0); }}><X className="w-4 h-4 mr-1" /> Clear</Button>
                </div>
                {/* Advanced Attribute Filters */}
                {showAdvancedFilters && attributes.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 border-t">
                    {attributes.map(attr => (
                      <div key={attr.id} className="space-y-1">
                        <Label className="text-xs">{attr.display_name}</Label>
                        <Input
                          placeholder={`Filter by ${attr.display_name}`}
                          value={contactFilters.attributes?.[attr.id] || ''}
                          onChange={(e) => setContactFilters(f => ({
                            ...f, attributes: { ...f.attributes, [attr.id]: e.target.value }
                          }))}
                          className="h-8 text-sm"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Selection Actions */}
              {selectedContacts.length > 0 && (
                <div className="flex items-center gap-3 mb-4 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <span className="text-sm font-medium text-emerald-700">{selectedContacts.length} selected</span>
                  <Button size="sm" onClick={() => openCampaignDialog(selectedContacts)}><Send className="w-3.5 h-3.5 mr-1.5" />Create Campaign</Button>
                  <Button size="sm" variant="outline" onClick={() => setSelectedContacts([])}>Clear</Button>
                </div>
              )}

              {/* Table */}
              <div className="border rounded-lg overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10"><Checkbox checked={selectAll} onCheckedChange={toggleSelectAll} /></TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Name</TableHead>
                      {attributes.slice(0, 4).map(attr => <TableHead key={attr.id}>{attr.display_name}</TableHead>)}
                      <TableHead>Tags</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="w-20">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contacts.length === 0 ? (
                      <TableRow><TableCell colSpan={7 + Math.min(attributes.length, 4)} className="text-center py-12 text-slate-500"><Users className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p>No contacts</p></TableCell></TableRow>
                    ) : (
                      contacts.map(contact => {
                        const contactTags = (contact.tags || []).map(tid => tags.find(t => t.id === tid)).filter(Boolean);
                        return (
                          <TableRow key={contact.id} className={selectedContacts.some(c => c.id === contact.id) ? 'bg-emerald-50' : ''}>
                            <TableCell><Checkbox checked={selectedContacts.some(c => c.id === contact.id)} onCheckedChange={() => toggleContactSelection(contact)} /></TableCell>
                            <TableCell className="font-mono">{contact.phone}</TableCell>
                            <TableCell>{contact.name || '-'}</TableCell>
                            {attributes.slice(0, 4).map(attr => <TableCell key={attr.id} className="text-sm">{contact.attributes?.[attr.id] || '-'}</TableCell>)}
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {contactTags.slice(0, 2).map(t => <Badge key={t.id} variant="outline" style={{ borderColor: t.color, color: t.color }} className="text-xs">{t.name}</Badge>)}
                                {contactTags.length > 2 && <Badge variant="outline" className="text-xs">+{contactTags.length - 2}</Badge>}
                              </div>
                            </TableCell>
                            <TableCell className="text-xs text-slate-500">{formatIST(contact.created_at)}</TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button variant="ghost" size="sm" onClick={() => { setEditingContact(contact); setContactForm({ phone: contact.phone, name: contact.name, tags: contact.tags, attributes: contact.attributes }); setContactDialog(true); }}><Edit2 className="w-3.5 h-3.5" /></Button>
                                <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm({ type: 'contact', item: contact })}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {contactsTotal > contactsLimit && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-slate-500">Showing {contactsPage * contactsLimit + 1} - {Math.min((contactsPage + 1) * contactsLimit, contactsTotal)} of {contactsTotal.toLocaleString()}</p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={contactsPage === 0} onClick={() => setContactsPage(p => p - 1)}><ChevronLeft className="w-4 h-4 mr-1" /> Prev</Button>
                    <Button variant="outline" size="sm" disabled={(contactsPage + 1) * contactsLimit >= contactsTotal} onClick={() => setContactsPage(p => p + 1)}>Next <ChevronRight className="w-4 h-4 ml-1" /></Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Attributes Tab */}
        <TabsContent value="attributes">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div><CardTitle>Custom Attributes</CardTitle><CardDescription>Define custom contact fields</CardDescription></div>
              <Button onClick={() => { setEditingAttribute(null); setAttributeForm({}); setAttributeDialog(true); }}><Plus className="w-4 h-4 mr-2" />Add</Button>
            </CardHeader>
            <CardContent>
              {attributes.length === 0 ? (
                <div className="text-center py-12 text-slate-500"><FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p>No attributes</p></div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {attributes.map(attr => (
                    <Card key={attr.id} className="border">
                      <CardContent className="pt-4 flex justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <span>{ATTRIBUTE_TYPES.find(t => t.value === attr.attribute_type)?.icon || '📝'}</span>
                            <h4 className="font-medium">{attr.display_name}</h4>
                          </div>
                          <p className="text-xs text-slate-500 font-mono mt-1">${attr.name}</p>
                          <Badge variant="outline" className="text-xs mt-2">{ATTRIBUTE_TYPES.find(t => t.value === attr.attribute_type)?.label}</Badge>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => { setEditingAttribute(attr); setAttributeForm(attr); setAttributeDialog(true); }}><Edit2 className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm({ type: 'attribute', item: attr })}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tags Tab */}
        <TabsContent value="tags">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div><CardTitle>Contact Tags</CardTitle></div>
              <Button onClick={() => { setEditingTag(null); setTagForm({ color: '#6366f1' }); setTagDialog(true); }}><Plus className="w-4 h-4 mr-2" />Add</Button>
            </CardHeader>
            <CardContent>
              {tags.length === 0 ? (
                <div className="text-center py-12 text-slate-500"><Tag className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p>No tags</p></div>
              ) : (
                <div className="flex flex-wrap gap-3">
                  {tags.map(t => (
                    <div key={t.id} className="flex items-center gap-2 px-4 py-2 rounded-full border bg-white shadow-sm" style={{ borderColor: t.color }}>
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: t.color }} />
                      <span className="font-medium">{t.name}</span>
                      <div className="flex gap-1 ml-2">
                        <button className="p-1 hover:bg-slate-100 rounded" onClick={() => { setEditingTag(t); setTagForm(t); setTagDialog(true); }}><Edit2 className="w-3.5 h-3.5 text-slate-500" /></button>
                        <button className="p-1 hover:bg-slate-100 rounded" onClick={() => setDeleteConfirm({ type: 'tag', item: t })}><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Campaigns Tab */}
        <TabsContent value="campaigns">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div><CardTitle>Campaigns</CardTitle></div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={refreshCampaigns}><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
                <Button onClick={() => openCampaignDialog([])} className="bg-emerald-600 hover:bg-emerald-700"><Plus className="w-4 h-4 mr-2" />Create</Button>
              </div>
            </CardHeader>
            <CardContent>
              {campaigns.length === 0 ? (
                <div className="text-center py-16 text-slate-500"><Send className="w-16 h-16 mx-auto mb-4 text-slate-300" /><p>No campaigns</p></div>
              ) : (
                <div className="space-y-4">
                  {campaigns.map(c => (
                    <Card key={c.id} className="border hover:shadow-md transition-shadow">
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="p-3 bg-emerald-100 rounded-lg"><Send className="w-6 h-6 text-emerald-600" /></div>
                            <div>
                              <h4 className="font-semibold text-lg">{c.name}</h4>
                              <p className="text-sm text-slate-500">{c.description || 'No description'}</p>
                              <div className="flex items-center gap-3 mt-2">
                                <CampaignStatusBadge status={c.status} />
                                <span className="text-sm text-slate-500"><Users className="w-4 h-4 inline mr-1" />{c.total_contacts?.toLocaleString()}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {c.status === 'draft' && <Button size="sm" onClick={() => startCampaign(c.id)}><Play className="w-4 h-4 mr-1" />Start</Button>}
                            {c.status === 'running' && <Button size="sm" variant="outline" onClick={() => pauseCampaign(c.id)}><Pause className="w-4 h-4 mr-1" />Pause</Button>}
                            {c.status === 'paused' && <Button size="sm" onClick={() => startCampaign(c.id)}><Play className="w-4 h-4 mr-1" />Resume</Button>}
                            <Button size="sm" variant="outline" onClick={() => openAnalytics(c)}><BarChart3 className="w-4 h-4 mr-1" />Analytics</Button>
                          </div>
                        </div>
                        {c.status === 'running' && c.total_contacts > 0 && (
                          <div className="mt-4">
                            <div className="flex justify-between text-sm mb-1"><span>Progress</span><span>{c.sent_count || 0} / {c.total_contacts}</span></div>
                            <Progress value={((c.sent_count || 0) / c.total_contacts) * 100} className="h-2" />
                          </div>
                        )}
                        {['completed', 'running', 'paused'].includes(c.status) && (
                          <div className="grid grid-cols-4 gap-4 mt-4 pt-4 border-t">
                            <div className="text-center"><p className="text-2xl font-bold text-blue-600">{c.sent_count || 0}</p><p className="text-xs text-slate-500">Sent</p></div>
                            <div className="text-center"><p className="text-2xl font-bold text-emerald-600">{c.delivered_count || 0}</p><p className="text-xs text-slate-500">Delivered</p></div>
                            <div className="text-center"><p className="text-2xl font-bold text-purple-600">{c.read_count || 0}</p><p className="text-xs text-slate-500">Read</p></div>
                            <div className="text-center"><p className="text-2xl font-bold text-red-600">{c.failed_count || 0}</p><p className="text-xs text-slate-500">Failed</p></div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="config">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Settings className="w-5 h-5" />API Configuration</CardTitle></CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Send Message API URL</Label><Input value={configForm.send_api_url || ''} onChange={(e) => setConfigForm(f => ({ ...f, send_api_url: e.target.value }))} placeholder="https://..." /></div>
                <div className="space-y-2"><Label>Templates API URL</Label><Input value={configForm.templates_api_url || ''} onChange={(e) => setConfigForm(f => ({ ...f, templates_api_url: e.target.value }))} placeholder="https://..." /></div>
              </div>
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-4">
                  <div><h4 className="font-medium">API Headers</h4></div>
                  <Button variant="outline" size="sm" onClick={() => setConfigForm(f => ({ ...f, api_headers: [...(f.api_headers || []), { name: '', value: '', enabled: true }] }))}><Plus className="w-4 h-4 mr-2" />Add</Button>
                </div>
                <div className="space-y-3">
                  {(configForm.api_headers || []).map((h, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <Switch checked={h.enabled !== false} onCheckedChange={(v) => { const n = [...(configForm.api_headers || [])]; n[idx] = { ...n[idx], enabled: v }; setConfigForm(f => ({ ...f, api_headers: n })); }} />
                      <Input value={h.name || ''} onChange={(e) => { const n = [...(configForm.api_headers || [])]; n[idx] = { ...n[idx], name: e.target.value }; setConfigForm(f => ({ ...f, api_headers: n })); }} placeholder="Header name" className="w-48" />
                      <Input value={h.value || ''} onChange={(e) => { const n = [...(configForm.api_headers || [])]; n[idx] = { ...n[idx], value: e.target.value }; setConfigForm(f => ({ ...f, api_headers: n })); }} placeholder="Value" className="flex-1" type={h.name?.toLowerCase().includes('key') ? 'password' : 'text'} />
                      <Button variant="ghost" size="sm" onClick={() => { const n = (configForm.api_headers || []).filter((_, i) => i !== idx); setConfigForm(f => ({ ...f, api_headers: n })); }}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="border-t pt-4">
                <h4 className="font-medium mb-4">Throughput Control</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2"><Label>Max Requests/Sec</Label><Input type="number" value={configForm.max_requests_per_second || 80} onChange={(e) => setConfigForm(f => ({ ...f, max_requests_per_second: Number(e.target.value) }))} /></div>
                  <div className="space-y-2"><Label>Pause (sec)</Label><Input type="number" value={configForm.rate_limit_pause_seconds || 10} onChange={(e) => setConfigForm(f => ({ ...f, rate_limit_pause_seconds: Number(e.target.value) }))} /></div>
                  <div className="space-y-2"><Label>Resume Speed</Label><Input type="number" value={configForm.rate_limit_resume_mps || 78} onChange={(e) => setConfigForm(f => ({ ...f, rate_limit_resume_mps: Number(e.target.value) }))} /></div>
                </div>
              </div>
              <div className="flex justify-end pt-4 border-t">
                <Button onClick={saveConfig} disabled={savingConfig}>{savingConfig ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      
      {/* Attribute Dialog */}
      <Dialog open={attributeDialog} onOpenChange={setAttributeDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingAttribute ? 'Edit' : 'New'} Attribute</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Name (code)</Label><Input value={attributeForm.name || ''} onChange={(e) => setAttributeForm(f => ({ ...f, name: e.target.value.toLowerCase().replace(/\s+/g, '_') }))} disabled={!!editingAttribute} /></div>
              <div className="space-y-2"><Label>Display Name</Label><Input value={attributeForm.display_name || ''} onChange={(e) => setAttributeForm(f => ({ ...f, display_name: e.target.value }))} /></div>
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={attributeForm.attribute_type || 'string'} onValueChange={(v) => setAttributeForm(f => ({ ...f, attribute_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{ATTRIBUTE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.icon} {t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setAttributeDialog(false)}>Cancel</Button><Button onClick={saveAttribute}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tag Dialog */}
      <Dialog open={tagDialog} onOpenChange={setTagDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingTag ? 'Edit' : 'New'} Tag</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Name</Label><Input value={tagForm.name || ''} onChange={(e) => setTagForm(f => ({ ...f, name: e.target.value }))} /></div>
            <div className="space-y-2"><Label>Color</Label><div className="flex flex-wrap gap-2">{TAG_COLORS.map(c => <button key={c} className={`w-8 h-8 rounded-full border-2 ${tagForm.color === c ? 'border-slate-800 scale-110' : 'border-transparent'}`} style={{ backgroundColor: c }} onClick={() => setTagForm(f => ({ ...f, color: c }))} />)}</div></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setTagDialog(false)}>Cancel</Button><Button onClick={saveTag}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contact Dialog */}
      <Dialog open={contactDialog} onOpenChange={setContactDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingContact ? 'Edit' : 'Add'} Contact</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Phone *</Label><Input value={contactForm.phone || ''} onChange={(e) => setContactForm(f => ({ ...f, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))} disabled={!!editingContact} /></div>
              <div className="space-y-2"><Label>Name</Label><Input value={contactForm.name || ''} onChange={(e) => setContactForm(f => ({ ...f, name: e.target.value }))} /></div>
            </div>
            {tags.length > 0 && (
              <div className="space-y-2">
                <Label>Tags</Label>
                <div className="flex flex-wrap gap-2">
                  {tags.map(t => <Button key={t.id} variant={contactForm.tags?.includes(t.id) ? 'default' : 'outline'} size="sm" onClick={() => setContactForm(f => ({ ...f, tags: f.tags?.includes(t.id) ? f.tags.filter(id => id !== t.id) : [...(f.tags || []), t.id] }))}><div className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: t.color }} />{t.name}</Button>)}
                </div>
              </div>
            )}
            {attributes.length > 0 && (
              <div className="space-y-3"><Label>Attributes</Label>
                {attributes.map(attr => <div key={attr.id} className="flex items-center gap-3"><Label className="w-32 text-sm">{attr.display_name}</Label><Input value={contactForm.attributes?.[attr.id] || ''} onChange={(e) => setContactForm(f => ({ ...f, attributes: { ...f.attributes, [attr.id]: e.target.value } }))} /></div>)}
              </div>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setContactDialog(false)}>Cancel</Button><Button onClick={saveContact} disabled={!contactForm.phone || contactForm.phone.length !== 10}>{editingContact ? 'Update' : 'Add'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle><FileSpreadsheet className="w-5 h-5 inline mr-2" />Upload Contacts</DialogTitle>
            <DialogDescription>Import contacts from a CSV file with attribute mapping</DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            {/* File Upload */}
            <div className="border-2 border-dashed rounded-lg p-6 text-center hover:border-emerald-400 transition-colors">
              <Input type="file" accept=".csv" onChange={handleFileChange} className="hidden" id="csv-upload" />
              <label htmlFor="csv-upload" className="cursor-pointer">
                <Upload className="w-10 h-10 mx-auto text-slate-400 mb-2" />
                <p className="text-sm font-medium">{csvFile ? csvFile.name : 'Click to upload CSV'}</p>
                <p className="text-xs text-slate-500 mt-1">Maximum 5 million contacts supported</p>
              </label>
            </div>

            {csvColumns.length > 0 && (
              <>
                {/* Required Fields */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium flex items-center gap-2">
                    <Info className="w-4 h-4 text-blue-500" />
                    Required Field Mapping
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm">Phone Column <span className="text-red-500">*</span></Label>
                      <Select value={uploadForm.phone_column || ''} onValueChange={(v) => setUploadForm(f => ({ ...f, phone_column: v }))}>
                        <SelectTrigger><SelectValue placeholder="Select phone column" /></SelectTrigger>
                        <SelectContent>{csvColumns.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">Name Column</Label>
                      <Select value={uploadForm.name_column || 'none'} onValueChange={(v) => setUploadForm(f => ({ ...f, name_column: v === 'none' ? '' : v }))}>
                        <SelectTrigger><SelectValue placeholder="Select name column" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {csvColumns.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Attribute Mapping */}
                {attributes.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium flex items-center gap-2">
                      <Layers className="w-4 h-4 text-purple-500" />
                      Custom Attribute Mapping
                      <span className="text-xs text-slate-500 font-normal">(Map CSV columns to contact attributes)</span>
                    </h4>
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            <TableHead className="text-xs w-1/2">CSV Column</TableHead>
                            <TableHead className="text-xs w-1/2">Map to Attribute</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {csvColumns
                            .filter(col => col !== uploadForm.phone_column && col !== uploadForm.name_column)
                            .map(col => (
                              <TableRow key={col}>
                                <TableCell className="py-2">
                                  <div className="flex items-center gap-2">
                                    <FileText className="w-3 h-3 text-slate-400" />
                                    <span className="text-sm font-medium">{col}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="py-2">
                                  <Select
                                    value={uploadForm.attribute_mapping?.[col] || 'none'}
                                    onValueChange={(v) => setUploadForm(f => ({
                                      ...f,
                                      attribute_mapping: {
                                        ...f.attribute_mapping,
                                        [col]: v === 'none' ? '' : v
                                      }
                                    }))}
                                  >
                                    <SelectTrigger className="h-8 text-sm">
                                      <SelectValue placeholder="Skip this column" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="none">
                                        <span className="text-slate-500">Skip this column</span>
                                      </SelectItem>
                                      {attributes.filter(a => a.is_active !== false).map(attr => (
                                        <SelectItem key={attr.id} value={attr.id}>
                                          <span className="flex items-center gap-2">
                                            {ATTRIBUTE_TYPES.find(t => t.value === attr.attribute_type)?.icon || '📝'}
                                            {attr.display_name}
                                          </span>
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* Tags Selection */}
                {tags.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium flex items-center gap-2">
                      <Tag className="w-4 h-4 text-orange-500" />
                      Add Tags to Imported Contacts
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {tags.filter(t => t.is_active !== false).map(tag => (
                        <Badge
                          key={tag.id}
                          variant="outline"
                          className={`cursor-pointer transition-colors ${
                            uploadForm.tag_ids?.includes(tag.id) 
                              ? 'border-2' 
                              : 'opacity-60 hover:opacity-100'
                          }`}
                          style={{ 
                            borderColor: tag.color,
                            backgroundColor: uploadForm.tag_ids?.includes(tag.id) ? `${tag.color}20` : 'transparent'
                          }}
                          onClick={() => {
                            const currentTags = uploadForm.tag_ids || [];
                            if (currentTags.includes(tag.id)) {
                              setUploadForm(f => ({ ...f, tag_ids: currentTags.filter(t => t !== tag.id) }));
                            } else {
                              setUploadForm(f => ({ ...f, tag_ids: [...currentTags, tag.id] }));
                            }
                          }}
                        >
                          <span style={{ color: tag.color }}>{tag.name}</span>
                          {uploadForm.tag_ids?.includes(tag.id) && <CheckCircle className="w-3 h-3 ml-1" style={{ color: tag.color }} />}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* CSV Preview */}
                {csvPreview.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium flex items-center gap-2">
                      <Eye className="w-4 h-4 text-slate-500" />
                      Data Preview
                      <span className="text-xs text-slate-500 font-normal">(First 5 rows)</span>
                    </h4>
                    <div className="border rounded-lg overflow-auto max-h-40">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            {csvColumns.slice(0, 6).map(c => (
                              <TableHead key={c} className="text-xs whitespace-nowrap">{c}</TableHead>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {csvPreview.map((r, i) => (
                            <TableRow key={i}>
                              {csvColumns.slice(0, 6).map(c => (
                                <TableCell key={c} className="text-xs py-1.5">{r[c] || '-'}</TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Upload Progress */}
            {uploading && (
              <div className="space-y-2 bg-blue-50 p-4 rounded-lg">
                <div className="flex justify-between text-sm">
                  <span className="font-medium">Uploading contacts...</span>
                  <span className="text-blue-600">{uploadProgress}%</span>
                </div>
                <Progress value={uploadProgress} className="h-2" />
              </div>
            )}
          </div>
          
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => { 
                setUploadDialog(false); 
                setCsvFile(null); 
                setCsvColumns([]); 
                setCsvPreview([]);
                setUploadForm({}); 
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={uploadContacts} 
              disabled={!csvFile || !uploadForm.phone_column || uploading}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {uploading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</>
              ) : (
                <><Upload className="w-4 h-4 mr-2" />Upload Contacts</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Campaign Creation Dialog */}
      <Dialog open={campaignDialog} onOpenChange={(o) => { if (!o) resetCampaignForm(); setCampaignDialog(o); }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle><Target className="w-5 h-5 inline mr-2" />Create Campaign - Step {campaignStep}/3</DialogTitle>
          </DialogHeader>
          <div className="flex items-center gap-2 mb-4">{[1,2,3].map(s => <><div key={s} className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${s === campaignStep ? 'bg-emerald-600 text-white' : s < campaignStep ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100'}`}>{s < campaignStep ? <CheckCircle className="w-5 h-5" /> : s}</div>{s < 3 && <div className={`w-16 h-1 rounded ${s < campaignStep ? 'bg-emerald-400' : 'bg-slate-200'}`} />}</>)}</div>

          {/* Step 1 */}
          {campaignStep === 1 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Name *</Label><Input value={campaignForm.name} onChange={(e) => setCampaignForm(f => ({ ...f, name: e.target.value }))} /></div>
                <div className="space-y-2"><Label>Description</Label><Input value={campaignForm.description} onChange={(e) => setCampaignForm(f => ({ ...f, description: e.target.value }))} /></div>
              </div>
              <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg"><Switch checked={campaignForm.is_multi_template} onCheckedChange={(v) => { setCampaignForm(f => ({ ...f, is_multi_template: v })); if (!v && selectedTemplates.length > 1) setSelectedTemplates(selectedTemplates.slice(0, 1)); }} /><div><Label>Multi-Template</Label><p className="text-sm text-slate-500">Rotate templates</p></div></div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between"><Label>Templates</Label>{campaignTemplatesHasMore && <Button variant="outline" size="sm" onClick={() => fetchCampaignTemplates(campaignTemplatesSkip, true)}><ChevronDown className="w-4 h-4 mr-1" />Load More</Button>}</div>
                {campaignTemplates.length === 0 ? (
                  <div className="p-8 border-2 border-dashed rounded-lg text-center"><MessageSquare className="w-10 h-10 mx-auto mb-2 text-slate-300" /><Button variant="link" onClick={() => fetchCampaignTemplates(0, false)}>Load Templates</Button></div>
                ) : (
                  <div className="grid grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 max-h-[300px] overflow-y-auto p-2">
                    {campaignTemplates.filter(t => t.status === 'APPROVED').map(t => <TemplateCard key={t.id} template={t} selected={selectedTemplates.some(st => st.id === t.id)} onSelect={toggleTemplateSelection} />)}
                  </div>
                )}
                {selectedTemplates.length > 0 && <p className="text-sm text-emerald-600">{selectedTemplates.length} selected</p>}
              </div>

              {/* Variables with $attribute support */}
              {selectedTemplates.length > 0 && selectedTemplates[0].total_parameters > 0 && (
                <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <Label>Variables</Label>
                    <p className="text-xs text-slate-500">Use $name, $phone, or $attributeName for dynamic values</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {Array.from({ length: selectedTemplates[0].total_parameters }, (_, i) => (
                      <div key={i} className="space-y-1">
                        <Label className="text-xs">{`{{${i + 1}}}`}</Label>
                        <Input value={campaignForm.template_variables?.[String(i + 1)] || ''} onChange={(e) => setCampaignForm(f => ({ ...f, template_variables: { ...f.template_variables, [String(i + 1)]: e.target.value } }))} placeholder={`e.g., Hello $name`} />
                      </div>
                    ))}
                  </div>
                  {/* Available attributes hint */}
                  {attributes.length > 0 && (
                    <div className="text-xs text-slate-500 flex flex-wrap gap-1">
                      <span>Available:</span>
                      <Badge variant="outline" className="text-[10px]">$name</Badge>
                      <Badge variant="outline" className="text-[10px]">$phone</Badge>
                      {attributes.map(a => <Badge key={a.id} variant="outline" className="text-[10px]">${a.name}</Badge>)}
                    </div>
                  )}
                </div>
              )}

              {/* Fallback values */}
              {extractedPlaceholders.length > 0 && (
                <div className="space-y-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-600" /><Label>Global Fallback Values</Label></div>
                  <p className="text-xs text-amber-700">Set default values if contact doesn&apos;t have the attribute</p>
                  <div className="grid grid-cols-2 gap-3">
                    {extractedPlaceholders.map(p => (
                      <div key={p} className="flex items-center gap-2">
                        <Label className="text-xs w-20">${p}</Label>
                        <Input value={campaignForm.fallback_values?.[p] || ''} onChange={(e) => setCampaignForm(f => ({ ...f, fallback_values: { ...f.fallback_values, [p]: e.target.value } }))} placeholder={`Default for ${p}`} className="h-8" />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Media URL */}
              {selectedTemplates.length > 0 && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(selectedTemplates[0].type) && (
                <div className="space-y-2"><Label>Media URL *</Label><Input value={campaignForm.media_url} onChange={(e) => setCampaignForm(f => ({ ...f, media_url: e.target.value }))} /></div>
              )}
            </div>
          )}

          {/* Step 2 */}
          {campaignStep === 2 && (
            <div className="space-y-6">
              <div className="grid grid-cols-3 gap-3">
                <Button variant={campaignContacts.length > 0 ? 'default' : 'outline'} className="h-auto py-4 flex-col" onClick={() => setCampaignForm(f => ({ ...f, audience_type: 'selected' }))}><Users className="w-6 h-6 mb-2" /><span>Selected</span><span className="text-xs">{campaignContacts.length}</span></Button>
                <Button variant={campaignForm.audience_type === 'filtered' && !campaignContacts.length ? 'default' : 'outline'} className="h-auto py-4 flex-col" onClick={() => { setCampaignContacts([]); setCampaignForm(f => ({ ...f, audience_type: 'filtered' })); }}><Filter className="w-6 h-6 mb-2" /><span>Filtered</span></Button>
                <Button variant={campaignForm.audience_type === 'all' && !campaignContacts.length ? 'default' : 'outline'} className="h-auto py-4 flex-col" onClick={() => { setCampaignContacts([]); setCampaignForm(f => ({ ...f, audience_type: 'all' })); }}><Target className="w-6 h-6 mb-2" /><span>All</span><span className="text-xs">{contactsTotal.toLocaleString()}</span></Button>
              </div>
              {campaignContacts.length > 0 && (
                <div className="border rounded-lg max-h-[200px] overflow-auto">
                  <Table><TableHeader><TableRow><TableHead>Phone</TableHead><TableHead>Name</TableHead><TableHead className="w-10"></TableHead></TableRow></TableHeader>
                    <TableBody>{campaignContacts.slice(0, 50).map(c => <TableRow key={c.id}><TableCell className="font-mono">{c.phone}</TableCell><TableCell>{c.name || '-'}</TableCell><TableCell><Button variant="ghost" size="sm" onClick={() => setCampaignContacts(prev => prev.filter(x => x.id !== c.id))}><X className="w-4 h-4" /></Button></TableCell></TableRow>)}</TableBody>
                  </Table>
                  {campaignContacts.length > 50 && <p className="text-center py-2 text-sm text-slate-500">+{campaignContacts.length - 50} more</p>}
                </div>
              )}
              <div className="space-y-2"><Label>Schedule</Label><Input type="datetime-local" value={campaignForm.scheduled_at || ''} onChange={(e) => setCampaignForm(f => ({ ...f, scheduled_at: e.target.value }))} className="w-64" /></div>
            </div>
          )}

          {/* Step 3 - Review with Preview and Validation */}
          {campaignStep === 3 && (
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-4">
                <Card><CardContent className="pt-4 space-y-3">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><span className="text-slate-500">Name:</span> <strong>{campaignForm.name}</strong></div>
                    <div><span className="text-slate-500">Templates:</span> <strong>{selectedTemplates.length}</strong></div>
                    <div><span className="text-slate-500">Audience:</span> <strong>{campaignContacts.length > 0 ? `${campaignContacts.length} selected` : campaignForm.audience_type === 'all' ? `All (${contactsTotal})` : 'Filtered'}</strong></div>
                    <div><span className="text-slate-500">Schedule:</span> <strong>{campaignForm.scheduled_at ? formatIST(campaignForm.scheduled_at) : 'Draft'}</strong></div>
                  </div>
                </CardContent></Card>

                {/* Validation Results */}
                {extractedPlaceholders.length > 0 && (
                  <Card>
                    <CardHeader className="py-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm">Data Validation</CardTitle>
                        <Button variant="outline" size="sm" onClick={validateContacts} disabled={validating}>
                          {validating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}Validate
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      {validationResult ? (
                        <div className="space-y-2 text-sm">
                          <p>Total: {validationResult.total_contacts}</p>
                          {validationResult.contacts_with_missing > 0 && (
                            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                              <div className="flex items-center gap-2 text-amber-800"><AlertTriangle className="w-4 h-4" /><strong>{validationResult.contacts_with_missing} contacts have missing values</strong></div>
                              <div className="mt-2 space-y-1">
                                {Object.entries(validationResult.missing_value_counts || {}).map(([attr, count]) => (
                                  count > 0 && <p key={attr} className="text-xs text-amber-700">${attr}: {count} missing</p>
                                ))}
                              </div>
                            </div>
                          )}
                          {validationResult.contacts_with_missing === 0 && (
                            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-2 text-emerald-800"><CheckCircle className="w-4 h-4" />All contacts have required data</div>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-500">Click validate to check for missing attribute values</p>
                      )}
                    </CardContent>
                  </Card>
                )}

                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-3"><AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" /><div><p className="font-medium text-amber-800">Before launch</p><ul className="text-sm text-amber-700 list-disc list-inside"><li>Messages sent at configured rate</li><li>Ensure API is configured</li><li>Can pause anytime</li></ul></div></div>
                </div>
              </div>

              <div>
                <Label className="mb-2 block">WhatsApp Preview</Label>
                {selectedTemplates.length > 0 ? (
                  <WhatsAppPreview template={selectedTemplates[0]} variables={campaignForm.template_variables} mediaUrl={campaignForm.media_url} contact={campaignContacts[0]} />
                ) : (
                  <div className="p-8 border rounded-lg text-center text-slate-500"><MessageSquare className="w-10 h-10 mx-auto mb-2 text-slate-300" /><p>No template</p></div>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="flex justify-between">
            <div>{campaignStep > 1 && <Button variant="outline" onClick={() => setCampaignStep(s => s - 1)}><ChevronLeft className="w-4 h-4 mr-2" />Back</Button>}</div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { resetCampaignForm(); setCampaignDialog(false); }}>Cancel</Button>
              {campaignStep < 3 ? (
                <Button onClick={() => setCampaignStep(s => s + 1)} disabled={(campaignStep === 1 && (!campaignForm.name || !selectedTemplates.length)) || (campaignStep === 2 && campaignForm.audience_type === 'selected' && !campaignContacts.length)}>Next<ChevronRight className="w-4 h-4 ml-2" /></Button>
              ) : (
                <Button onClick={createCampaign} className="bg-emerald-600 hover:bg-emerald-700"><Send className="w-4 h-4 mr-2" />{campaignForm.scheduled_at ? 'Schedule' : 'Create'}</Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Analytics Dialog with Contacts */}
      <Dialog open={analyticsDialog} onOpenChange={setAnalyticsDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle><BarChart3 className="w-5 h-5 inline mr-2" />Campaign Analytics</DialogTitle></DialogHeader>
          {analyticsLoading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin" /></div>
          ) : analyticsData ? (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div><h3 className="text-xl font-semibold">{analyticsData.name}</h3><p className="text-slate-500">{analyticsData.description}</p></div>
                <CampaignStatusBadge status={analyticsData.status} />
              </div>

              {/* Completion Percentage */}
              <div className="grid grid-cols-2 gap-4">
                <Card><CardContent className="pt-4">
                  <div className="flex items-center justify-between mb-2"><span className="text-sm">Completion</span><span className="text-2xl font-bold text-emerald-600">{analyticsData.completion_percentage}%</span></div>
                  <Progress value={analyticsData.completion_percentage} className="h-3" />
                </CardContent></Card>
                <Card><CardContent className="pt-4">
                  <div className="flex items-center justify-between mb-2"><span className="text-sm">Delivery Rate</span><span className="text-2xl font-bold text-blue-600">{analyticsData.delivery_percentage}%</span></div>
                  <Progress value={analyticsData.delivery_percentage} className="h-3" />
                </CardContent></Card>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-5 gap-4">
                <Card><CardContent className="pt-4 text-center"><p className="text-2xl font-bold">{analyticsData.total_contacts?.toLocaleString()}</p><p className="text-xs text-slate-500">Total</p></CardContent></Card>
                <Card><CardContent className="pt-4 text-center"><p className="text-2xl font-bold text-blue-600">{analyticsData.message_stats?.sent || 0}</p><p className="text-xs text-slate-500">Sent</p></CardContent></Card>
                <Card><CardContent className="pt-4 text-center"><p className="text-2xl font-bold text-emerald-600">{analyticsData.message_stats?.delivered || 0}</p><p className="text-xs text-slate-500">Delivered</p></CardContent></Card>
                <Card><CardContent className="pt-4 text-center"><p className="text-2xl font-bold text-purple-600">{analyticsData.message_stats?.read || 0}</p><p className="text-xs text-slate-500">Read</p></CardContent></Card>
                <Card><CardContent className="pt-4 text-center"><p className="text-2xl font-bold text-red-600">{analyticsData.message_stats?.failed || 0}</p><p className="text-xs text-slate-500">Failed</p></CardContent></Card>
              </div>

              {/* Error Summary */}
              {analyticsData.error_summary?.length > 0 && (
                <Card>
                  <CardHeader className="py-3"><CardTitle className="text-sm text-red-600"><AlertCircle className="w-4 h-4 inline mr-1" />Errors</CardTitle></CardHeader>
                  <CardContent className="pt-0">
                    <div className="space-y-2">
                      {analyticsData.error_summary.map((e, i) => (
                        <div key={i} className="flex items-center justify-between p-2 bg-red-50 rounded text-sm">
                          <span className="text-red-700 truncate flex-1">{e.error}</span>
                          <Badge variant="destructive">{e.count}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Contacts List */}
              <Card>
                <CardHeader className="py-3"><CardTitle className="text-sm">Contacts ({analyticsData.contacts?.total || 0})</CardTitle></CardHeader>
                <CardContent className="pt-0">
                  <div className="border rounded-lg max-h-[300px] overflow-auto">
                    <Table>
                      <TableHeader><TableRow><TableHead>Phone</TableHead><TableHead>Name</TableHead><TableHead>Status</TableHead><TableHead>Sent At</TableHead><TableHead>Error</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {(analyticsData.contacts?.items || []).map((c, i) => (
                          <TableRow key={i}>
                            <TableCell className="font-mono">{c.phone}</TableCell>
                            <TableCell>{c.name || '-'}</TableCell>
                            <TableCell><Badge className={c.status === 'sent' || c.status === 'delivered' ? 'bg-emerald-100 text-emerald-700' : c.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-slate-100'}>{c.status}</Badge></TableCell>
                            <TableCell className="text-xs">{c.sent_at ? formatIST(c.sent_at) : '-'}</TableCell>
                            <TableCell className="text-xs text-red-600 max-w-[200px] truncate" title={c.error_message}>{c.error_message || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  {analyticsData.contacts?.has_more && (
                    <div className="flex justify-center mt-4">
                      <Button variant="outline" size="sm" onClick={loadMoreAnalyticsContacts}><ChevronDown className="w-4 h-4 mr-1" />Load More</Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Timing */}
              <div className="grid grid-cols-3 gap-4 text-sm">
                {analyticsData.started_at && <div><span className="text-slate-500">Started:</span> {formatIST(analyticsData.started_at)}</div>}
                {analyticsData.completed_at && <div><span className="text-slate-500">Completed:</span> {formatIST(analyticsData.completed_at)}</div>}
                {analyticsData.duration_seconds && <div><span className="text-slate-500">Duration:</span> {Math.round(analyticsData.duration_seconds / 60)} min</div>}
              </div>
            </div>
          ) : null}
          <DialogFooter><Button variant="outline" onClick={() => setAnalyticsDialog(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete {deleteConfirm?.type}?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => {
              if (deleteConfirm?.type === 'attribute') deleteAttribute(deleteConfirm.item.id);
              else if (deleteConfirm?.type === 'tag') deleteTag(deleteConfirm.item.id);
              else if (deleteConfirm?.type === 'contact') deleteContact(deleteConfirm.item);
            }}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default WACampaignsPage;
